import { mString } from 'lib/helpers/utility';

import DrAvatar from './lib/components/DrAvatar';
import AmountBadge from './lib/components/AmountBadge';
import InfoCallout from './lib/components/InfoCallout';
import SlideHeader from './lib/components/SlideHeader';
import SpeechBubble from './lib/components/SpeechBubble';

const AllowedAmountPage = ({ estimate }) => {
  const {
    allowed = 0,
    providerCharges = 0,
    writeOff = 0,
    deductible = 0,
    coinsurance = 0,
    coinsuranceRate = 0,
    copay = 0
  } = estimate ?? {};

  // The base coinsurance is applied to: allowed − copay − deductible.
  const coinsuranceBase = Math.max(0, allowed - copay - deductible);
  const insuranceSharePct = Math.max(0, 100 - coinsuranceRate);

  const amountCards = [
    { label: 'Remaining Deductible', amount: deductible },
    coinsurance > 0 && { label: `Coinsurance (${coinsuranceRate}%)`, amount: coinsurance },
    copay > 0 && { label: 'Copay', amount: copay }
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-5">
      <SlideHeader title="Your balance" />

      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-primary-800">
          Your insurance's negotiated rate (allowed amount) for today's visit is
        </p>
        <AmountBadge amount={allowed} size="small" />
      </div>

      <SpeechBubble variant="default">
        The full charge for your visit is {mString(providerCharges)}, but your insurance has a
        negotiated <span className="font-600 italic">allowed amount</span> of {mString(allowed)}{' '}
        with us. The {mString(writeOff)} difference is a write-off — you're not on the hook for it.
      </SpeechBubble>

      {coinsurance > 0 && (
        <div className="flex items-end gap-4">
          <DrAvatar size="medium" variant={3} className="shrink-0" />
          <SpeechBubble variant="highlight" tail="left">
            After your remaining {mString(deductible)} deductible, your insurance covers{' '}
            {insuranceSharePct}% of the rest. You pay the other {coinsuranceRate}% — your{' '}
            <span className="font-600 italic">coinsurance</span> — which on the remaining{' '}
            {mString(coinsuranceBase)} comes to {mString(coinsurance)}.
          </SpeechBubble>
        </div>
      )}

      <div className="flex gap-3 sm:flex-col">
        {amountCards.map((card) => (
          <div
            key={card.label}
            className="flex-1 rounded-xl border border-solid border-neutral-200 bg-white p-4">
            <p className="text-sm font-500 uppercase text-neutral-500">{card.label}</p>
            <p className="text-lg font-700 text-primary-900">{mString(card.amount)}</p>
          </div>
        ))}
      </div>

      <InfoCallout />
    </div>
  );
};

export default AllowedAmountPage;
